import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { Button, Checkbox, colors, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { NotificationImportant } from '@insurancemenu/imenu-components-lib/dist/icons';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { SelectedPlanInProductLine } from 'types/electionService';
import { createScrollEffectStyles } from 'components/common/styles/verticalScrollEffect';
import { createStyles } from './styles';
import BatchGuidelines from '../BatchGuidelines/index';

interface BatchApplyModalProps {
  show: boolean;
  onClose: () => void;
}

const BatchApplyModal = observer(({ show, onClose }: BatchApplyModalProps): JSX.Element => {
  const classes = createStyles();
  const scrollEffectClasses = createScrollEffectStyles();

  const [selectedPlansInProductLine, setSelectedPlansInProductLine] = useState<SelectedPlanInProductLine[]>([]);
  const {
    employeeElectionsStore: { selectedSubscribersIds, subscribers, applyBatchChanges, products },
  } = useRootStore();

  const convertedProducts = products?.map((product) => toJS(product)).slice();
  const convertedSubscribers = subscribers?.map((subscriber) => toJS(subscriber)).slice();
  const convertedSelectedSubscribersIds = selectedSubscribersIds?.map((id) => id).slice();
  const selectedSubscribers = convertedSubscribers.filter(({ id }) => convertedSelectedSubscribersIds.includes(id));

  const query = useQueryParams();
  const groupId = query.get('groupId') || '';

  useEffect(() => {
    const initialPlansInfo = convertedProducts?.map(({ productLineId }): SelectedPlanInProductLine => {
      return { productLineId, selectedPlanId: '', selectedPlanName: '' };
    });
    setSelectedPlansInProductLine(initialPlansInfo || []);
  }, [convertedProducts?.length]);

  const applyChanges = (): void => {
    if (convertedSelectedSubscribersIds.length) applyBatchChanges(groupId, selectedPlansInProductLine);
    onClose();
  };

  const getPlanCheckboxStatus = (productLineId: ProductLineId, planId: string): boolean => {
    return selectedPlansInProductLine.some((planInfo) => {
      return planInfo.productLineId === productLineId && planInfo.selectedPlanId === planId;
    });
  };

  const selectPlan = (productLineId: ProductLineId, planId: string, planName: string): void => {
    const updatedPlansData = selectedPlansInProductLine.map((planInfo: SelectedPlanInProductLine) => {
      if (planInfo.productLineId === productLineId) {
        return { productLineId, selectedPlanId: planId, selectedPlanName: planName };
      }
      return planInfo;
    });
    setSelectedPlansInProductLine(updatedPlansData);
  };

  const getPlanStyles = (productLineId: ProductLineId, isPlanSelected: boolean): string[] => {
    const isPlanSelectedInProductLine = selectedPlansInProductLine.some((planInfo: SelectedPlanInProductLine) => {
      return planInfo.productLineId === productLineId && planInfo.selectedPlanId;
    });

    const notSelectedPlanStyles =
      !isPlanSelected && isPlanSelectedInProductLine ? clsx(classes.plan, classes.notSelectedPlan) : '';
    const anySelectedPlanStyles = !isPlanSelected && !isPlanSelectedInProductLine ? classes.plan : '';
    const selectedPlanStyles = isPlanSelected ? clsx(classes.plan, classes.selectedPlan) : '';

    return [selectedPlanStyles, notSelectedPlanStyles, anySelectedPlanStyles];
  };

  const checkSubscribersForSameClass = (): boolean => {
    const selectedClass = selectedSubscribers.find(({ employeeClass }) => employeeClass)?.employeeClass || '';
    // if selected subscribers have no classes or they are the same, subscribers can use batch apply functional
    // if selected subscribers have different classes, they should reselect subscribers with the same class or change plans manually

    return selectedSubscribers.every(({ employeeClass }): boolean => {
      if (selectedClass && employeeClass) return employeeClass === selectedClass;
      if (!selectedClass) return true;
      return false;
    });
  };

  const areSubscribersWithTheSameClass = checkSubscribersForSameClass();

  // create product lines sections with products
  const productLineSections = convertedProducts?.map(({ productLineId, plans: plansInProductLine }): JSX.Element => {
    const productLinePlans = plansInProductLine
      // filter products, which are available for ee class (if selected subscribers have the same class or don't have any one)
      // each Product has a list of classes, for which it's available
      .filter(
        ({ eligibleClasses }) =>
          areSubscribersWithTheSameClass &&
          (eligibleClasses.includes(selectedSubscribers[0]?.employeeClass) || !selectedSubscribers[0]?.employeeClass),
      )
      // create plan component for filtered products
      .map(({ id, name }): JSX.Element => {
        const isPlanSelected = getPlanCheckboxStatus(productLineId, id);
        const planStyles = getPlanStyles(productLineId, isPlanSelected).join('');

        return (
          <div key={id} className={planStyles}>
            <Checkbox
              label={name}
              checked={isPlanSelected}
              onChange={(): void => {
                selectPlan(productLineId, id, name);
              }}
            />
          </div>
        );
      });

    const isWaivedPlanSelected = getPlanCheckboxStatus(productLineId, 'null');
    const planStyles = getPlanStyles(productLineId, isWaivedPlanSelected).join('');

    // create section component for each product line with list of plans inside
    return (
      <div className={classes.plansSection} key={productLineId}>
        <div className={classes.productLineNameWrapper}>
          {PRODUCT_LINES_CONTENT_MAP[productLineId].icon('2.4rem')}
          <span className={classes.productLineName}>{PRODUCT_LINES_CONTENT_MAP[productLineId].label}</span>
        </div>
        <div className={classes.plansWrapper}>
          {/* created dynamic plans */}
          {productLinePlans}
          {/* static waive plan */}
          <div className={planStyles}>
            <Checkbox
              label="Waived"
              checked={isWaivedPlanSelected}
              onChange={(): void => {
                selectPlan(productLineId, 'null', 'Waive Coverage');
              }}
            />
          </div>
        </div>
      </div>
    );
  });

  const createBatchModalContent = (): JSX.Element => {
    if (areSubscribersWithTheSameClass) {
      return (
        <>
          <p className={classes.applyPlansText}>
            <span>Apply Plans to </span>
            <span className={classes.employeesCount}>
              {`${convertedSelectedSubscribersIds.length} ${
                convertedSelectedSubscribersIds.length === 1 ? 'Employee' : 'Employees'
              }`}
            </span>
          </p>
          <div className={clsx(classes.productLineSectionWrapper, scrollEffectClasses.scrollEffect)}>
            {productLineSections}
          </div>
          <div className={classes.buttonsWrapper}>
            <Button color="primary" type="outlined" className={classes.cancelBtn} onClick={onClose}>
              Cancel
            </Button>
            <Button color="primary" className={classes.actionBtn} onClick={applyChanges}>
              Apply
            </Button>
          </div>
        </>
      );
    }

    // warning modal content
    return (
      <div className={classes.warningContent}>
        <NotificationImportant width="4.5rem" height="4.5rem" color={colors.orange[500]} className={classes.icon} />
        <p className={clsx(classes.warningText, classes.differentClassesText)}>
          Batch actions CANNOT be used across employees in different classes.
        </p>
        <Button
          color="primary"
          className={clsx(classes.cancelWarningModalBtn, classes.differentClassesBtn)}
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    );
  };

  return (
    <div className={show ? clsx(classes.show, classes.modal) : classes.hide}>
      {selectedSubscribersIds.length === 0 ? <BatchGuidelines onClose={onClose} /> : createBatchModalContent()}
    </div>
  );
});

export default BatchApplyModal;
